import React from "react";
import { ArrowRight, RotateCw, Triangle, Columns, ListOrdered } from "lucide-react";

const THEME = {
  steps: { bg: "from-sage/10 to-sage/5", border: "border-sage/30", accent: "bg-sage text-white", Icon: ListOrdered, label: "Process" },
  cycle: { bg: "from-terracotta/10 to-terracotta/5", border: "border-terracotta/30", accent: "bg-terracotta text-white", Icon: RotateCw, label: "Cycle" },
  pyramid: { bg: "from-amber-400/10 to-amber-400/5", border: "border-amber-400/30", accent: "bg-amber-600 text-white", Icon: Triangle, label: "Hierarchy" },
  comparison: { bg: "from-indigo-400/10 to-indigo-400/5", border: "border-indigo-400/30", accent: "bg-indigo-600 text-white", Icon: Columns, label: "Comparison" },
};

/**
 * One-component infographic used after each lesson's sections.
 * Renders as:
 *  - steps      → horizontal ordered flow with arrows
 *  - cycle      → circular SVG with items placed at equal angles
 *  - pyramid    → stacked widening bands (bottom wider)
 *  - comparison → two-column grid
 */
export default function Infographic({ data }) {
  if (!data || !Array.isArray(data.items) || data.items.length === 0) return null;
  const kind = THEME[data.kind] ? data.kind : "steps";
  const t = THEME[kind];
  const Icon = t.Icon;
  const items = data.items.slice(0, 8);

  return (
    <section
      className={`rounded-3xl border ${t.border} bg-gradient-to-br ${t.bg} p-6 sm:p-8`}
      data-testid={`lesson-infographic-${kind}`}
    >
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-10 h-10 rounded-xl ${t.accent} flex items-center justify-center`}>
          <Icon className="w-5 h-5" strokeWidth={1.8} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">{t.label}</div>
          <h3 className="font-display text-lg sm:text-xl font-semibold tracking-tight">{data.title}</h3>
        </div>
      </div>

      {kind === "steps" && <Steps items={items} accent={t.accent} />}
      {kind === "cycle" && <Cycle items={items} accent={t.accent} />}
      {kind === "pyramid" && <Pyramid items={items} accent={t.accent} />}
      {kind === "comparison" && <Comparison items={items} accent={t.accent} />}
    </section>
  );
}

function Steps({ items, accent }) {
  return (
    <div className="flex flex-wrap items-stretch gap-3">
      {items.map((it, i) => (
        <React.Fragment key={`step-${i}`}>
          <div className="flex-1 min-w-[160px] bg-white border border-border rounded-2xl p-4 flex flex-col gap-2">
            <div className={`w-7 h-7 rounded-full ${accent} inline-flex items-center justify-center text-xs font-bold`}>
              {i + 1}
            </div>
            <div className="font-medium text-sm leading-tight">{it.label}</div>
            {it.detail && <div className="text-xs text-muted-foreground leading-relaxed">{it.detail}</div>}
          </div>
          {i < items.length - 1 && (
            <div className="self-center text-muted-foreground hidden sm:block">
              <ArrowRight className="w-5 h-5" strokeWidth={1.8} />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function Cycle({ items, accent }) {
  const n = items.length;
  const R = 150;
  const cx = 220;
  const cy = 190;
  const size = 380;
  return (
    <div className="flex justify-center">
      <svg viewBox={`0 0 ${2 * cx} ${size}`} className="w-full max-w-[520px] h-auto">
        <circle cx={cx} cy={cy} r={R - 8} fill="none" stroke="#c4614a" strokeWidth="2" strokeDasharray="4 6" opacity="0.5" />
        {items.map((it, i) => {
          const a = (i / n) * Math.PI * 2 - Math.PI / 2;
          const x = cx + Math.cos(a) * R;
          const y = cy + Math.sin(a) * R;
          return (
            <g key={`cyc-${i}`}>
              <circle cx={x} cy={y} r="36" fill="#fff" stroke="#c4614a" strokeWidth="2" />
              <text x={x} y={y - 2} textAnchor="middle" fontSize="11" fontWeight="700" fill="#6f2d21">
                {i + 1}
              </text>
              <text x={x} y={y + 12} textAnchor="middle" fontSize="9" fill="#6f2d21">
                {truncate(it.label, 10)}
              </text>
            </g>
          );
        })}
      </svg>
      <ol className="sr-only">
        {items.map((it, i) => <li key={`cyc-sr-${i}`}>{it.label} — {it.detail}</li>)}
      </ol>
    </div>
  );
}

function Pyramid({ items, accent: _accent }) {
  // Bottom of the pyramid first (widest, most foundational).
  const ordered = [...items].reverse();
  return (
    <div className="flex flex-col items-center gap-1.5">
      {ordered.map((it, i) => {
        const widthPct = 40 + (i / Math.max(1, ordered.length - 1)) * 55;
        return (
          <div
            key={`pyr-${i}`}
            className="rounded-xl px-4 py-2.5 text-center text-sm font-medium border border-amber-500/40 bg-white"
            style={{ width: `${widthPct}%`, minWidth: 180 }}
          >
            <div className="text-amber-700 text-[10px] uppercase tracking-[0.2em] font-bold">
              Level {ordered.length - i}
            </div>
            <div className="font-semibold">{it.label}</div>
            {it.detail && <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{it.detail}</div>}
          </div>
        );
      })}
    </div>
  );
}

function Comparison({ items }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {items.map((it, i) => (
        <div key={`cmp-${i}`} className="bg-white border border-border rounded-2xl p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-indigo-600 font-bold mb-1">
            {i % 2 === 0 ? "Option A" : "Option B"}
          </div>
          <div className="font-semibold text-sm mb-1">{it.label}</div>
          {it.detail && <div className="text-xs text-muted-foreground leading-relaxed">{it.detail}</div>}
        </div>
      ))}
    </div>
  );
}

function truncate(s, n) {
  s = String(s || "");
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}
